/**
 * Helpers de búsqueda de caravanas.
 */

/**
 * Caracteres de envoltura que se descartan al pegar términos de búsqueda
 * (p. ej. copiados desde Excel, Word o listas formateadas):
 * comillas dobles/simples, corchetes y paréntesis, incluyendo las variantes
 * tipográficas (" " ' ').
 */
const WRAPPING_CHARS_REGEX = /["'[\]()“”‘’]/g;

/**
 * Quita los caracteres de envoltura (comillas, [], (), '') del término de
 * búsqueda pegado, de modo que '"JJJJ2"' o '["JJJJ2","JJJJ3"]' se busquen
 * como 'JJJJ2' / 'JJJJ2,JJJJ3'. Las comas se conservan: son el separador
 * de lista de caravanas.
 */
function stripWrappingChars(term: string): string {
  return term.replace(WRAPPING_CHARS_REGEX, '');
}

/**
 * Indica si una caravana matchea contra un término de búsqueda.
 *
 * Regla de matching (alineada con el flujo "Revisar animales"):
 *  - Si el término contiene comas (lista de caravanas, p. ej. "C-1, C-2"),
 *    se exige **match exacto** (case-insensitive) por cada token. El usuario
 *    separa con comas cuando conoce la caravana con precisión, así que no se
 *    admiten coincidencias parciales que harían aparecer animales cuyo ID
 *    contiene como subcadena otra caravana (p. ej. "C-10" ante "C-1").
 *  - Si el término NO contiene comas, se permite **match parcial**
 *    (case-insensitive), para búsqueda libre cuando el usuario no recuerda
 *    la caravana completa.
 *
 * Antes de comparar se descartan los caracteres de envoltura pegados
 * (comillas, [], (), ''), para tolerar términos copiados desde otras apps.
 *
 * @param caravana   Caravana del animal.
 * @param searchTerm Término de búsqueda (uno o varios separados por coma).
 */
export function matchesCaravanaSearch(caravana: string, searchTerm: string): boolean {
  if (!caravana || !searchTerm) return false;

  const caravanaLower = caravana.toLowerCase();
  const q = stripWrappingChars(searchTerm).toLowerCase();

  // Lista de caravanas separadas por coma → solo match exacto por token.
  // Quien lista con comas conoce la caravana con precisión, así que no se
  // admiten coincidencias parciales (evita que "C-10" aparezca ante "C-1").
  if (q.includes(',')) {
    const terms = q.split(',').map(t => t.trim()).filter(Boolean);
    return terms.some(term => term === caravanaLower);
  }

  // Término único → match parcial (búsqueda libre).
  return caravanaLower.includes(q);
}
